import { c as _c } from "react/compiler-runtime";
import type { NavActiveState } from '@sprd/spreadshirt-shell';
import { useEffect, useMemo, useState } from 'react';
import { useLocale } from 'next-intl';
import { usePathname as useSlug } from 'next/navigation';
import { HeaderContent, LinkLevel1, LinkLevel2 } from '@/types/headerHierarchy';
import { HeaderContentPro, LinkLevel1Pro } from '@/types/spreadshirtProHeader';
import { usePathname } from '@/i18n/navigation';
import { getIsLocalizedCyoPath, getRelativeUrl, isCYOItem } from '@/utility/headerNavigationHelpers';
import { useClientSearchParams } from './useClientSearchParams';
import { usePageChrome } from './usePageChrome';
const buildLevelUrlMap = (levels: LinkLevel1[] | LinkLevel1Pro[]) => {
  const map: Record<string, string[]> = {};
  for (const level1 of levels) {
    const level1Urls: string[] = [];
    if (level1.content.link.url) {
      level1Urls.push(getRelativeUrl(level1.content.link.url));
    }
    const level2Items = level1.content.level2 || [];
    for (const level2 of level2Items) {
      const level2Urls: string[] = [];
      if ('link' in level2.content && level2.content.link.url) {
        level2Urls.push(getRelativeUrl(level2.content.link.url));
        level1Urls.push(getRelativeUrl(level2.content.link.url));
      }
      if ('headerTeaserSlot' in level2.content) {
        level2.content.headerTeaserSlot?.headerTeaser?.tiles.map(tile => {
          const url = getRelativeUrl(tile.link.url);
          level2Urls.push(url);
          level1Urls.push(url);
        });
      }
      if ('level3' in level2.content && level2.content.level3) {
        for (const level3 of level2.content.level3) {
          level3.content.column?.forEach(link => {
            const url = getRelativeUrl(link.content.link.url);
            level2Urls.push(url);
            level1Urls.push(url);
          });
        }
      }
      map[level2.content._meta.deliveryId] = level2Urls;
    }
    map[level1.content._meta.deliveryId] = level1Urls;
  }
  return map;
};

/**
 * Which header entries the current route marks as active — the routing-aware half
 * of the navigation state. The shell owns open/close; this only names keys.
 */
export const useNavActiveState = content => {
  const $ = _c(12);
  const levels = content.content.level1;
  const {
    pageType
  } = usePageChrome();
  const locale = useLocale();
  const intlPathname = usePathname();
  const slug = useSlug();
  const searchParams = useClientSearchParams();
  const [hash, setHash] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setHash(window.location.hash);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== slug) {
    t1 = [slug];
    $[1] = slug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t0, t1);
  const currentUrl = slug + (searchParams.size ? `?${searchParams}` : "") + hash;
  let t2;
  if ($[3] !== levels) {
    t2 = buildLevelUrlMap(levels);
    $[3] = levels;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const urlMap = t2;
  let t3;
  if ($[5] !== currentUrl || $[6] !== intlPathname || $[7] !== levels || $[8] !== locale || $[9] !== pageType || $[10] !== urlMap) {
    const activeKeys = [];
    const persistentKeys = [];
    const openKeys = [];
    levels.forEach((level1, index) => {
      const key = level1.content._meta.deliveryId;
      const level2Items = level1.content.level2 || [];
      const isCYOLevel = level2Items.some(_temp);
      const isLevel1Active = index === 0 && pageType === "CYO" || index === 1 && pageType === "MP";
      if (isLevel1Active) {
        activeKeys.push(key);
        persistentKeys.push(key);
      }
      if (isCYOLevel && getIsLocalizedCyoPath(locale, intlPathname)) {
        openKeys.push(key);
      }
      for (const level2 of level2Items) {
        if (!("link" in level2.content)) {
          continue;
        }
        const level2Key = level2.content._meta.deliveryId;
        if ((urlMap[level2Key] ?? []).includes(currentUrl)) {
          activeKeys.push(level2Key);
        }
      }
    });
    t3 = {
      activeKeys,
      persistentKeys,
      openKeys
    };
    $[5] = currentUrl;
    $[6] = intlPathname;
    $[7] = levels;
    $[8] = locale;
    $[9] = pageType;
    $[10] = urlMap;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
function _temp(item) {
  return "link" in item.content && isCYOItem(item as LinkLevel2);
}